// Upload many product images at once.
//
// Two ways to say which photo belongs to which product:
//
//   1. Name the file after the SKU — "OPH-L#HPAFCLEAR-18.00.jpg". Matching
//      ignores case, spaces and punctuation, because file systems and people
//      mangle SKUs ("OPH-L_HPAFCLEAR-18.00.JPG" still matches).
//   2. Upload an Excel sheet with a SKU column and a file-name column, for
//      photos that already have other names (IMG_0412.jpg). Download the
//      sheet from here: every product is listed, only the file name is typed.
//
// Pictures pasted INTO an Excel file are not read — spreadsheet libraries do
// not expose embedded images reliably — so Excel only carries the mapping.
//
// Each file is shrunk in the browser and uploaded one at a time with its own
// status, so one bad photo does not stop the rest.

import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Images, FolderOpen, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Checkbox } from '@/components/ui/Checkbox';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { updateProduct } from '@/lib/api/products';
import { downloadBlob } from '@/lib/api/invoice';
import { loadXlsx, IMPORT_ACCEPT } from '@/lib/spreadsheet';
import { useAuth } from '@/hooks/useAuth';
import {
  IMAGE_ACCEPT, baseName, isImageFile, removeStoredImage, skuKey, uploadProductImage,
} from '@/lib/productImages';
import { cn, formatNumber } from '@/lib/format';
import type { Product } from '@/types/domain';

type RowState = 'siap' | 'ganti' | 'lewati' | 'tidak-cocok' | 'dobel' | 'uploading' | 'selesai' | 'gagal';

interface Row {
  file: File;
  product?: Product;
  via?: 'nama file' | 'Excel';
  state: RowState;
  message?: string;
}

export interface BulkImageUploadDialogProps {
  open: boolean;
  onClose: () => void;
  products: Product[];
}

export function BulkImageUploadDialog({ open, onClose, products }: BulkImageUploadDialogProps) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const filesRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const excelRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<File[]>([]);
  /** file base name (lower case) → SKU, from the optional Excel sheet. */
  const [mapping, setMapping] = useState<Map<string, string> | null>(null);
  const [mappingName, setMappingName] = useState<string | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [progress, setProgress] = useState<Map<File, { state: RowState; message?: string }>>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bySkuKey = useMemo(() => {
    const m = new Map<string, Product[]>();
    for (const p of products) {
      const k = skuKey(p.sku);
      m.set(k, [...(m.get(k) ?? []), p]);
    }
    return m;
  }, [products]);

  const rows: Row[] = useMemo(() => {
    const claimed = new Set<string>();
    return files.map((file) => {
      const done = progress.get(file);
      const name = baseName(file.name).toLowerCase();
      let product: Product | undefined;
      let via: Row['via'];
      let message: string | undefined;

      const mappedSku = mapping?.get(name) ?? mapping?.get(file.name.toLowerCase());
      if (mappedSku) {
        const hits = bySkuKey.get(skuKey(mappedSku)) ?? [];
        if (hits.length === 1) { product = hits[0]; via = 'Excel'; }
        else message = hits.length === 0 ? `SKU "${mappedSku}" di Excel tidak ada di katalog` : `SKU "${mappedSku}" cocok ke beberapa produk`;
      }
      if (!product && !message) {
        const hits = bySkuKey.get(skuKey(baseName(file.name))) ?? [];
        if (hits.length === 1) { product = hits[0]; via = 'nama file'; }
        else if (hits.length > 1) message = `Nama file cocok ke ${hits.length} produk — pakai Excel untuk memilih`;
        else message = 'Tidak ada SKU yang cocok dengan nama file';
      }

      if (done) return { file, product, via, state: done.state, message: done.message ?? message };
      if (!product) return { file, state: 'tidak-cocok', message };
      if (claimed.has(product.id)) {
        return { file, product, via, state: 'dobel', message: 'Produk ini sudah dapat foto lain di daftar ini' };
      }
      claimed.add(product.id);
      if (product.imageUrl) {
        return replaceExisting
          ? { file, product, via, state: 'ganti' }
          : { file, product, via, state: 'lewati', message: 'Sudah punya gambar' };
      }
      return { file, product, via, state: 'siap' };
    });
  }, [files, mapping, bySkuKey, replaceExisting, progress]);

  const counts = useMemo(() => {
    const c: Record<RowState, number> = { siap: 0, ganti: 0, lewati: 0, 'tidak-cocok': 0, dobel: 0, uploading: 0, selesai: 0, gagal: 0 };
    for (const r of rows) c[r.state] += 1;
    return c;
  }, [rows]);
  const toUpload = rows.filter((r) => r.state === 'siap' || r.state === 'ganti');

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const images = [...list].filter(isImageFile);
    const skipped = list.length - images.length;
    setError(skipped > 0 ? `${skipped} file bukan gambar dan dilewati.` : null);
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}`));
      return [...prev, ...images.filter((f) => !seen.has(`${f.name}|${f.size}`))];
    });
  };

  const readMapping = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const XLSX = await loadXlsx();
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const table = XLSX.utils.sheet_to_json<(string | number)[]>(ws, { header: 1, blankrows: false, raw: false });
      const header = (table[0] ?? []).map((h) => String(h).toLowerCase());
      const skuCol = header.findIndex((h) => h.includes('sku'));
      const fileCol = header.findIndex((h) => h.includes('file') || h.includes('gambar'));
      if (skuCol === -1 || fileCol === -1) {
        throw new Error('Excel harus punya kolom "SKU" dan kolom "Nama file gambar". Pakai template dari tombol di sebelahnya.');
      }
      const m = new Map<string, string>();
      for (const row of table.slice(1)) {
        const sku = String(row[skuCol] ?? '').trim();
        const fname = String(row[fileCol] ?? '').trim();
        if (!sku || !fname) continue;
        m.set(baseName(fname).toLowerCase(), sku);
        m.set(fname.toLowerCase(), sku);
      }
      setMapping(m);
      setMappingName(`${file.name} · ${formatNumber(m.size / 2)} baris`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Excel tidak bisa dibaca.');
    }
  };

  const downloadMappingTemplate = async () => {
    try {
      const XLSX = await loadXlsx();
      const list = [...products].filter((p) => p.isActive).sort((a, b) => a.name.localeCompare(b.name));
      const ws = XLSX.utils.aoa_to_sheet([
        ['SKU', 'Nama produk', 'Nama file gambar', 'Gambar sekarang'],
        ...list.map((p) => [p.sku, p.name, '', p.imageUrl ? 'ada' : '']),
      ]);
      ws['!cols'] = [{ wch: 28 }, { wch: 60 }, { wch: 28 }, { wch: 16 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Gambar produk');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      downloadBlob(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'template-gambar-produk.xlsx');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal membuat template');
    }
  };

  const upload = async () => {
    if (!user || toUpload.length === 0) return;
    setBusy(true);
    setError(null);
    for (const r of toUpload) {
      setProgress((m) => new Map(m).set(r.file, { state: 'uploading' }));
      try {
        const url = await uploadProductImage(r.file, r.product!.id);
        await updateProduct(r.product!.id, {
          imageUrl: url,
          updatedBy: user.uid,
          updatedByName: user.displayName ?? undefined,
          updatedByEmail: user.email ?? undefined,
        });
        if (r.product!.imageUrl) await removeStoredImage(r.product!.imageUrl);
        setProgress((m) => new Map(m).set(r.file, { state: 'selesai' }));
      } catch (e) {
        setProgress((m) => new Map(m).set(r.file, { state: 'gagal', message: e instanceof Error ? e.message : 'Gagal' }));
      }
    }
    await qc.invalidateQueries({ queryKey: ['products'] });
    setBusy(false);
  };

  const reset = () => {
    setFiles([]); setMapping(null); setMappingName(null); setProgress(new Map()); setError(null);
  };
  const finished = counts.selesai + counts.gagal > 0 && !busy;

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) { reset(); onClose(); } }}
      title="Upload gambar produk"
      description="Banyak foto sekaligus. Foto dicocokkan ke produk lewat nama file = SKU, atau lewat Excel (SKU → nama file)."
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={busy}>
            {finished ? 'Tutup' : 'Batal'}
          </Button>
          <Button
            variant="primary"
            onClick={upload}
            loading={busy}
            disabled={busy || toUpload.length === 0}
            leftIcon={<Images size={16} />}
          >
            {toUpload.length > 0 ? `Upload ${formatNumber(toUpload.length)} gambar` : 'Upload'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <input ref={filesRef} type="file" accept={IMAGE_ACCEPT} multiple className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          <input
            ref={folderRef}
            type="file"
            multiple
            className="hidden"
            // Non-standard but supported by Chrome, Edge, Safari and Firefox.
            {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />
          <input ref={excelRef} type="file" accept={IMPORT_ACCEPT} className="hidden"
            onChange={(e) => { void readMapping(e.target.files?.[0]); e.target.value = ''; }} />
          <Button variant="primary" leftIcon={<Images size={16} />} onClick={() => filesRef.current?.click()} disabled={busy}>
            Pilih foto
          </Button>
          <Button variant="secondary" leftIcon={<FolderOpen size={16} />} onClick={() => folderRef.current?.click()} disabled={busy}>
            Pilih folder
          </Button>
          <span className="w-px h-6 bg-line mx-1" aria-hidden />
          <Button variant="secondary" leftIcon={<FileSpreadsheet size={16} />} onClick={() => excelRef.current?.click()} disabled={busy}>
            Excel SKU → nama file (opsional)
          </Button>
          <Button variant="ghost" leftIcon={<Download size={16} />} onClick={() => void downloadMappingTemplate()} disabled={busy}>
            Template Excel
          </Button>
          {mappingName && <Chip variant="info">{mappingName}</Chip>}
        </div>

        <div className="p-3 rounded border border-line bg-canvas text-caption text-ink-subtle space-y-1">
          <p>
            <b className="text-ink">Cara 1 — nama file = SKU:</b> contoh <code>OPH-L#HPAFCLEAR-18.00.jpg</code>. Huruf besar/kecil,
            spasi, dan tanda baca diabaikan saat mencocokkan.
          </p>
          <p>
            <b className="text-ink">Cara 2 — Excel:</b> download template, isi kolom <i>Nama file gambar</i> (mis. <code>IMG_0412.jpg</code>)
            di baris produknya, upload Excel-nya, lalu pilih foto-fotonya.
          </p>
          <p>Foto yang ditempel <i>di dalam</i> Excel tidak bisa dibaca — Excel hanya untuk memetakan nama file.</p>
        </div>

        <Checkbox
          checked={replaceExisting}
          onChange={setReplaceExisting}
          disabled={busy}
          label="Ganti gambar produk yang sudah ada"
          description="Kalau dimatikan, produk yang sudah punya gambar dilewati."
        />

        {error && <p role="alert" className="text-caption text-danger">{error}</p>}

        {rows.length > 0 && (
          <>
            <div className="flex flex-wrap gap-2">
              {counts.siap > 0 && <Chip variant="success">{formatNumber(counts.siap)} gambar baru</Chip>}
              {counts.ganti > 0 && <Chip variant="info">{formatNumber(counts.ganti)} mengganti</Chip>}
              {counts.lewati > 0 && <Chip variant="default">{formatNumber(counts.lewati)} dilewati</Chip>}
              {counts['tidak-cocok'] + counts.dobel > 0 && (
                <Chip variant="warning">{formatNumber(counts['tidak-cocok'] + counts.dobel)} tidak dipakai</Chip>
              )}
              {counts.selesai > 0 && <Chip variant="success">{formatNumber(counts.selesai)} selesai</Chip>}
              {counts.gagal > 0 && <Chip variant="danger">{formatNumber(counts.gagal)} gagal</Chip>}
            </div>
            <div className="max-h-80 overflow-auto border border-line rounded">
              <Table>
                <THead>
                  <TR>
                    <TH>File</TH>
                    <TH>Produk</TH>
                    <TH>Status</TH>
                  </TR>
                </THead>
                <TBody>
                  {rows.map((r) => (
                    <TR key={`${r.file.name}|${r.file.size}`} className={cn((r.state === 'tidak-cocok' || r.state === 'dobel') && 'opacity-70')}>
                      <TD className="font-mono text-caption break-all">{r.file.name}</TD>
                      <TD>
                        {r.product ? (
                          <>
                            <p className="text-small text-ink truncate max-w-[280px]">{r.product.name}</p>
                            <p className="text-caption text-ink-subtle">
                              <code>{r.product.sku}</code>{r.via && ` · via ${r.via}`}
                            </p>
                          </>
                        ) : (
                          <span className="text-caption text-ink-subtle">—</span>
                        )}
                      </TD>
                      <TD>
                        {r.state === 'siap' && <Chip variant="success">Baru</Chip>}
                        {r.state === 'ganti' && <Chip variant="info">Ganti</Chip>}
                        {r.state === 'lewati' && <span className="text-caption text-ink-subtle">{r.message}</span>}
                        {(r.state === 'tidak-cocok' || r.state === 'dobel') && (
                          <span className="text-caption text-warning inline-flex items-center gap-1">
                            <AlertTriangle size={12} /> {r.message}
                          </span>
                        )}
                        {r.state === 'uploading' && (
                          <span className="text-caption text-ink-subtle inline-flex items-center gap-1">
                            <Loader2 size={12} className="animate-spin" /> Upload…
                          </span>
                        )}
                        {r.state === 'selesai' && (
                          <span className="text-caption text-success inline-flex items-center gap-1">
                            <CheckCircle2 size={12} /> Selesai
                          </span>
                        )}
                        {r.state === 'gagal' && <span className="text-caption text-danger">{r.message}</span>}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
